/**
 * Attachments in the event window: files picked from the bar are listed at once and uploaded on Save (a new event
 * first needs its id); removed ones are deleted on Save. Plus the location suggestions (recent places, rooms).
 */
import { useI18n } from '@platform/i18n';
import { Badge, Button, formatBytes } from '@platform/ui';
import { PaperclipIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, type CalendarEventDetail } from '../api';

const LOCATIONS_KEY = 'platform.webmail.calendar.locations';
const MAX_LOCATIONS = 20;

export function recentLocations(): string[] {
  try {
    return (JSON.parse(globalThis.localStorage?.getItem(LOCATIONS_KEY) ?? '[]') as unknown[]).filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

export function rememberLocation(location: string): void {
  if (!location) return;
  try {
    globalThis.localStorage?.setItem(LOCATIONS_KEY, JSON.stringify([location, ...recentLocations().filter((l) => l !== location)].slice(0, MAX_LOCATIONS)));
  } catch {
    // Suggestions only.
  }
}

interface Entry {
  key: string;
  name: string;
  size: number;
  /** Not uploaded yet. */
  file?: File;
  /** Already on the event. */
  id?: string;
}

const base64Of = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result.replace(/^data:[^,]*,/, '') : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

export function useEditorAttachments(editing: CalendarEventDetail | undefined): { pick: () => void; save: (eventId: string) => Promise<void>; view: ReactNode; dirty: boolean } {
  const { t, formatNumber } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);

  useEffect(() => {
    if (!editing?.hasAttachments) return;
    let cancelled = false;
    api.eventAttachments(editing.id).then(
      (list) => !cancelled && setEntries((current) => [...list.map((a) => ({ key: a.id, name: a.name, size: a.size, id: a.id })), ...current]),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [editing?.id, editing?.hasAttachments]);

  const save = async (eventId: string) => {
    for (const id of removed) await api.removeEventAttachment(eventId, id);
    for (const entry of entries) {
      if (!entry.file) continue;
      await api.addEventAttachment(eventId, { name: entry.file.name, contentType: entry.file.type || 'application/octet-stream', content: await base64Of(entry.file) });
    }
  };

  const view = (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label={t('webmail.calendarUx.editor.attach')}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          setEntries((current) => [...current, ...files.map((file) => ({ key: `${file.name}-${file.size}-${Math.random()}`, name: file.name, size: file.size, file }))]);
          e.target.value = '';
        }}
      />
      {entries.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('webmail.calendarUx.editor.attachments')}>
          {entries.map((entry) => (
            <li key={entry.key}>
              <Badge variant="secondary" className="gap-1 pr-0.5">
                <PaperclipIcon aria-hidden className="size-3" />
                {entry.name}
                <span className="text-muted-foreground">{formatBytes(entry.size, formatNumber)}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="size-5"
                  aria-label={t('webmail.calendarUx.editor.removeAttachment', { name: entry.name })}
                  onClick={() => {
                    setEntries((current) => current.filter((x) => x !== entry));
                    if (entry.id) setRemoved((current) => [...current, entry.id as string]);
                  }}
                >
                  <XIcon />
                </Button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return { pick: () => input.current?.click(), save, view, dirty: removed.length > 0 || entries.some((entry) => entry.file !== undefined) };
}

/** Download links of an event's attachments (event panel). */
export function EventAttachmentList({ eventId }: { eventId: string }) {
  const { t, formatNumber } = useI18n();
  const [list, setList] = useState<{ id: string; name: string; size: number }[]>();
  useEffect(() => {
    let cancelled = false;
    api.eventAttachments(eventId).then((value) => !cancelled && setList(value), () => !cancelled && setList([]));
    return () => {
      cancelled = true;
    };
  }, [eventId]);
  if (!list || list.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1" aria-label={t('webmail.calendarUx.editor.attachments')}>
      {list.map((a) => (
        <li key={a.id}>
          <a href={api.eventAttachmentUrl(eventId, a.id)} download className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <PaperclipIcon aria-hidden className="size-3.5" />
            {a.name}
            <span className="text-xs text-muted-foreground">{formatBytes(a.size, formatNumber)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}
